import { toPersianDigits } from './persian-date.js';
import {
  customerServiceDiscountPercent,
  type CustomerServiceDefinition,
} from './customer-services.js';

/**
 * Iranian currency formatting: the single IRR→toman conversion and the Persian
 * number/grouping used wherever a price is shown.
 *
 * Lives in `contracts/` (not `site/`) because two very different renderers need
 * it: the static site generator *and* the embeddable widget's shadow DOM. Like
 * `persian-date.ts` it is pure and dependency-free, so the exact same string is
 * produced in Node at build time and in the browser at runtime.
 *
 * ## The unit, in one place
 *
 * Amounts in the catalog and on the payment/request rows are **rial**
 * (`currency: 'IRR'`) — the unit the gateway is charged and the admin panel
 * shows. Iranians price consultations in **toman**, and 1 toman is 10 rial, so
 * every toman figure a visitor reads is derived here by `irrToToman()`. The
 * number Core charges (10,000,000 IRR) and «۱٬۰۰۰٬۰۰۰ تومان» on a button are
 * therefore the *same* amount in two units, never two different prices.
 *
 * Deliberately no `Intl`: a locale formatter depends on the runtime's ICU data
 * and could render differently across environments.
 */

/** 1 toman is 10 rial. The only place this ratio appears. */
export const RIAL_PER_TOMAN = 10;

/** The Iranian thousands separator, «٬». */
const GROUP_SEPARATOR = '\u066C';

/** A stored rial amount → whole toman. Rial amounts are multiples of ten. */
export function irrToToman(rial: number): number {
  return Math.round(rial / RIAL_PER_TOMAN);
}

/** `10000000` (rial) → `۱٬۰۰۰٬۰۰۰`. Unit-free, for labelling by the caller. */
export function formatTomanNumber(rial: number): string {
  const grouped = String(irrToToman(rial)).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  return toPersianDigits(grouped);
}

/** `10000000` (rial) → `۱٬۰۰۰٬۰۰۰ تومان`. */
export function formatToman(rial: number): string {
  return `${formatTomanNumber(rial)} تومان`;
}

/** `33` → `۳۳٪ تخفیف`. */
export function formatDiscount(percent: number): string {
  return `${toPersianDigits(percent)}٪ تخفیف`;
}

/** A struck-through list price, a sale price, and the discount between them. */
export interface PriceSummary {
  /** `۱٬۰۰۰٬۰۰۰ تومان` — the amount the customer actually pays. */
  saleFa: string;
  /** `۱٬۵۰۰٬۰۰۰ تومان` — presentation only; `null` when not on offer. */
  listFa: string | null;
  /** `۳۳٪ تخفیف`; `null` when not on offer. Derived, never stored. */
  discountFa: string | null;
}

/**
 * The price a customer sees for one section, derived from the catalog.
 *
 * `null` when the section is unknown or has no usable amount, so a caller can
 * render nothing rather than a fabricated number. When present, `saleFa` is the
 * price the backend charges (`defaultAmount`, or the registry override that Core
 * applies before writing the request row) — so the figure shown here and the
 * amount the payment carries always agree.
 */
export function priceSummaryFor(
  definition: CustomerServiceDefinition | null | undefined,
): PriceSummary | null {
  if (!definition || !Number.isFinite(definition.defaultAmount)) return null;
  const percent = customerServiceDiscountPercent(definition);
  const onOffer = percent !== null && definition.listAmount !== undefined;
  return {
    saleFa: formatToman(definition.defaultAmount),
    listFa: onOffer ? formatToman(definition.listAmount!) : null,
    discountFa: onOffer ? formatDiscount(percent!) : null,
  };
}
